import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiVideo, FiPhone, FiMapPin, FiCalendar, FiCheckCircle, FiArrowLeft, FiArrowRight, FiShield, FiAlertCircle, FiUser, FiMail, FiSmartphone } from 'react-icons/fi';
import { useToast } from '../components/Toast';

function BookingModern({ user }) {
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const lawyerIdParam = searchParams.get('lawyer_id');
  const [currentStep, setCurrentStep] = useState(1);
  const [serviceType, setServiceType] = useState('video');
  const [duration, setDuration] = useState('60');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('11:00 AM');
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [lawyerInfo, setLawyerInfo] = useState(null);
  const [allLawyers, setAllLawyers] = useState([]);
  const [selectedLawyerId, setSelectedLawyerId] = useState(lawyerIdParam || '');
  const [lawyerLoading, setLawyerLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [bookingId, setBookingId] = useState(null);

  // Guest user details (for non-logged-in users)
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpVerified, setOtpVerified] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);

  const isLoggedIn = !!user?.id;

  // Fetch all lawyers list
  useEffect(() => {
    const fetchLawyers = async () => {
      try {
        setLawyerLoading(true);
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/lawyers`);
        const data = await res.json();
        if (data.success && data.data.length > 0) {
          setAllLawyers(data.data);
          const targetId = lawyerIdParam;
          const found = targetId ? data.data.find(l => String(l.id) === String(targetId)) : null;
          const selected = found || data.data[0];
          setLawyerInfo({
            id: selected.id,
            name: selected.full_name,
            specialty: selected.specialty || 'General Practice',
            avatar: selected.profile_image ? `${process.env.REACT_APP_API_URL}${selected.profile_image}` : null,
          });
          setSelectedLawyerId(String(selected.id));
        }
      } catch (err) {
        console.error('Failed to fetch lawyers:', err);
      } finally {
        setLawyerLoading(false);
      }
    };
    fetchLawyers();
  }, [lawyerIdParam]);

  const handleLawyerChange = (id) => {
    const found = allLawyers.find(l => String(l.id) === String(id));
    if (found) {
      setSelectedLawyerId(String(found.id));
      setLawyerInfo({
        id: found.id,
        name: found.full_name,
        specialty: found.specialty || 'General Practice',
        avatar: found.profile_image ? `${process.env.REACT_APP_API_URL}${found.profile_image}` : null,
      });
    }
  };

  if (user?.user_type === 'lawyer') {
    return (
      <div className="min-h-screen bg-secondary-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-card border border-secondary-100 p-10 text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-danger-50 flex items-center justify-center mx-auto mb-4">
            <FiAlertCircle className="w-8 h-8 text-danger-500" />
          </div>
          <h2 className="text-xl font-bold text-secondary-900 mb-2">Access Restricted</h2>
          <p className="text-secondary-500 mb-6">Lawyers cannot book consultations. This feature is available for clients only.</p>
          <button onClick={() => navigate('/dashboard')} className="btn-primary gap-2">
            <FiArrowLeft className="w-4 h-4" /> Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const timeSlots = [
    { time: '9:00 AM', available: true }, { time: '10:00 AM', available: true },
    { time: '11:00 AM', available: true }, { time: '2:00 PM', available: true },
    { time: '3:00 PM', available: true }, { time: '4:00 PM', available: false },
    { time: '5:00 PM', available: true }, { time: '6:00 PM', available: true }
  ];

  const handleNext = (e) => {
    e?.preventDefault();
    if (currentStep === 2 && !selectedDate) { toast.warning('Please select a date and time'); return; }
    if (currentStep < 3) setCurrentStep(currentStep + 1);
  };
  const handlePrev = () => { if (currentStep > 1) setCurrentStep(currentStep - 1); };

  // Send OTP to phone (simulated with email-based OTP via backend)
  const handleSendOtp = async () => {
    if (!guestPhone || guestPhone.length < 10) {
      toast.warning('Please enter a valid 10-digit mobile number');
      return;
    }
    if (!guestEmail) {
      toast.warning('Please enter your email address first');
      return;
    }
    setSendingOtp(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/bookings/send-otp.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: guestPhone, email: guestEmail, name: guestName })
      });
      const data = await res.json();
      if (data.success) {
        setOtpSent(true);
        toast.success(data.message || 'OTP sent to your mobile number. Please enter it below.');
      } else {
        toast.error(data.message || 'Failed to send OTP');
      }
    } catch (err) {
      console.error('OTP send error:', err);
      toast.error('Failed to send OTP. Please try again.');
    } finally {
      setSendingOtp(false);
    }
  };

  // Verify OTP
  const handleVerifyOtp = async () => {
    if (!otpCode || otpCode.length < 4) {
      toast.warning('Please enter the OTP');
      return;
    }
    setVerifyingOtp(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/bookings/verify-otp.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: guestPhone, otp: otpCode })
      });
      const data = await res.json();
      if (data.success) {
        setOtpVerified(true);
      } else {
        toast.error(data.message || 'Invalid OTP. Please try again.');
      }
    } catch (err) {
      console.error('OTP verify error:', err);
      toast.error('Verification failed. Please try again.');
    } finally {
      setVerifyingOtp(false);
    }
  };

  // Submit booking
  const handleBooking = async (e) => {
    e.preventDefault();
    if (!selectedDate || !agreeTerms) { toast.warning('Please fill in all required fields and agree to terms'); return; }

    // For guest users, must verify phone
    if (!isLoggedIn) {
      if (!guestName || !guestEmail || !guestPhone) {
        toast.warning('Please fill in your name, email and phone number');
        return;
      }
      if (!otpVerified) {
        toast.warning('Please verify your phone number with OTP first');
        return;
      }
    }

    setSubmitting(true);
    try {
      const bookingData = {
        lawyer_id: parseInt(selectedLawyerId),
        service_type: serviceType,
        consultation_date: `${selectedDate} ${selectedTime}`,
        duration_minutes: parseInt(duration),
        amount: 0,
        notes: notes,
        description: description,
      };

      if (isLoggedIn) {
        bookingData.user_id = user.id;
      } else {
        bookingData.guest_name = guestName;
        bookingData.guest_email = guestEmail;
        bookingData.guest_phone = guestPhone;
      }

      const url = isLoggedIn
        ? `${process.env.REACT_APP_API_URL}/api/bookings/?user_id=${user.id}`
        : `${process.env.REACT_APP_API_URL}/api/bookings/guest.php`;

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bookingData)
      });
      const data = await res.json();
      if (data.success) {
        setBookingSuccess(true);
        setBookingId(data.booking_id);
      } else {
        toast.error(data.message || 'Failed to create booking');
      }
    } catch (err) {
      console.error('Booking error:', err);
      toast.error('Failed to create booking. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Success screen
  if (bookingSuccess) {
    return (
      <div className="min-h-screen bg-secondary-50 flex items-center justify-center p-4">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-2xl shadow-card border border-secondary-100 p-10 text-center max-w-md">
          <div className="w-20 h-20 rounded-full bg-teal-50 flex items-center justify-center mx-auto mb-6">
            <FiCheckCircle className="w-10 h-10 text-teal-500" />
          </div>
          <h2 className="text-2xl font-bold text-secondary-900 mb-2">Booking Confirmed!</h2>
          <p className="text-secondary-500 mb-2">Your consultation has been scheduled successfully.</p>
          {bookingId && <p className="text-sm text-primary-600 font-semibold mb-4">Booking ID: #{bookingId}</p>}
          <div className="bg-secondary-50 rounded-xl p-4 mb-6 text-left space-y-2">
            <div className="flex justify-between text-sm"><span className="text-secondary-500">Lawyer</span><span className="font-medium text-secondary-900">{lawyerInfo?.name}</span></div>
            <div className="flex justify-between text-sm"><span className="text-secondary-500">Service</span><span className="font-medium text-secondary-900">{serviceType === 'video' ? 'Video Call' : serviceType === 'phone' ? 'Phone Call' : 'In-Person'}</span></div>
            <div className="flex justify-between text-sm"><span className="text-secondary-500">Date</span><span className="font-medium text-secondary-900">{selectedDate}</span></div>
            <div className="flex justify-between text-sm"><span className="text-secondary-500">Time</span><span className="font-medium text-secondary-900">{selectedTime}</span></div>
          </div>
          <p className="text-xs text-secondary-400 mb-6">A confirmation email has been sent to {isLoggedIn ? 'your registered email' : guestEmail}.</p>
          <div className="flex gap-3">
            {isLoggedIn ? (
              <button onClick={() => navigate('/dashboard')} className="flex-1 btn-primary py-3 gap-2"><FiCheckCircle className="w-4 h-4" /> Go to Dashboard</button>
            ) : (
              <button onClick={() => navigate('/')} className="flex-1 btn-primary py-3 gap-2"><FiCheckCircle className="w-4 h-4" /> Back to Home</button>
            )}
            <button onClick={() => navigate('/lawyers')} className="flex-1 btn-ghost py-3 border border-secondary-200">Browse Lawyers</button>
          </div>
        </motion.div>
      </div>
    );
  }

  const steps = [
    { num: 1, label: 'Service', icon: <FiVideo className="w-4 h-4" /> },
    { num: 2, label: 'Schedule', icon: <FiCalendar className="w-4 h-4" /> },
    { num: 3, label: isLoggedIn ? 'Review' : 'Your Details', icon: isLoggedIn ? <FiCheckCircle className="w-4 h-4" /> : <FiUser className="w-4 h-4" /> },
  ];

  const services = [
    { id: 'video', icon: <FiVideo className="w-6 h-6" />, name: 'Video Call', desc: 'Face-to-face video consultation', color: 'from-primary-500 to-primary-600' },
    { id: 'phone', icon: <FiPhone className="w-6 h-6" />, name: 'Phone Call', desc: 'Phone-based consultation', color: 'from-teal-500 to-teal-600' },
    { id: 'inperson', icon: <FiMapPin className="w-6 h-6" />, name: 'In-Person', desc: 'Office consultation', color: 'from-accent-500 to-accent-600' },
  ];

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Hero */}
      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0f1f35 0%, #1e3a5f 50%, #172e4d 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-20 w-64 h-64 rounded-full" style={{ background: 'radial-gradient(circle, rgba(212,175,55,0.4) 0%, transparent 70%)' }} />
        </div>
        <div className="max-w-6xl mx-auto px-4 py-14 text-center relative">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-3xl md:text-4xl font-bold text-white mb-3">Book Your Consultation</motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-white/50 text-lg">Schedule a professional legal consultation</motion.p>
        </div>
      </div>

      {/* Steps Indicator */}
      <div className="max-w-3xl mx-auto px-4 -mt-6 relative z-10">
        <div className="bg-white rounded-2xl shadow-card border border-secondary-100 p-4 flex items-center justify-between">
          {steps.map((step, i) => (
            <React.Fragment key={step.num}>
              <div className="flex items-center gap-3 cursor-pointer" onClick={() => { if (step.num < currentStep) setCurrentStep(step.num); }}>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold transition-all ${
                  currentStep > step.num ? 'bg-teal-500 text-white' : currentStep === step.num ? 'bg-primary-500 text-white' : 'bg-secondary-100 text-secondary-400'
                }`}>
                  {currentStep > step.num ? <FiCheckCircle className="w-5 h-5" /> : step.num}
                </div>
                <span className={`text-sm font-medium hidden sm:block ${currentStep >= step.num ? 'text-secondary-900' : 'text-secondary-400'}`}>{step.label}</span>
              </div>
              {i < steps.length - 1 && <div className={`flex-1 h-0.5 mx-2 rounded ${currentStep > step.num ? 'bg-teal-500' : 'bg-secondary-200'}`} />}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Column */}
          <div className="lg:col-span-2">
            <motion.div key={currentStep} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="bg-white rounded-2xl shadow-card border border-secondary-100 p-6 md:p-8">

              {/* Step 1: Service */}
              {currentStep === 1 && (
                <div className="space-y-6">
                  <div><h2 className="text-xl font-bold text-secondary-900 mb-1">Select Service Type</h2><p className="text-secondary-500 text-sm">Choose the consultation type that fits your needs</p></div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {services.map(s => (
                      <div key={s.id} onClick={() => setServiceType(s.id)} className={`relative p-5 rounded-xl border-2 cursor-pointer transition-all ${serviceType === s.id ? 'border-primary-500 bg-primary-50/50 shadow-md' : 'border-secondary-100 hover:border-secondary-200'}`}>
                        {serviceType === s.id && <div className="absolute top-3 right-3"><FiCheckCircle className="w-5 h-5 text-primary-500" /></div>}
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${s.color} flex items-center justify-center text-white mb-3`}>{s.icon}</div>
                        <h3 className="font-semibold text-secondary-900 mb-1">{s.name}</h3>
                        <p className="text-xs text-secondary-500 mb-2">{s.desc}</p>
                      </div>
                    ))}
                  </div>
                  <div>
                    <label className="label">Session Duration</label>
                    <div className="flex gap-3">
                      {['30', '60', '90', '120'].map(dur => (
                        <button key={dur} type="button" onClick={() => setDuration(dur)} className={`flex-1 py-2.5 rounded-xl text-sm font-medium border transition-all ${duration === dur ? 'bg-primary-500 text-white border-primary-500' : 'border-secondary-200 text-secondary-600 hover:border-primary-300'}`}>
                          {dur === '120' ? '2 hrs' : `${dur} min`}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div><label className="label">Describe Your Issue</label><textarea className="textarea" rows="4" placeholder="Briefly describe your legal concern..." value={description} onChange={(e) => setDescription(e.target.value)} /></div>
                </div>
              )}

              {/* Step 2: Schedule */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  <div><h2 className="text-xl font-bold text-secondary-900 mb-1">Select Date & Time</h2><p className="text-secondary-500 text-sm">Choose your preferred consultation slot</p></div>
                  <div><label className="label">Select Date</label><input type="date" className="input" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} min={new Date().toISOString().split('T')[0]} /></div>
                  <div>
                    <label className="label">Available Time Slots</label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {timeSlots.map(slot => (
                        <button key={slot.time} type="button" disabled={!slot.available} onClick={() => slot.available && setSelectedTime(slot.time)}
                          className={`p-3 rounded-xl border text-center transition-all ${!slot.available ? 'bg-secondary-50 border-secondary-100 text-secondary-300 cursor-not-allowed' : selectedTime === slot.time ? 'bg-primary-500 border-primary-500 text-white shadow-md' : 'border-secondary-200 text-secondary-600 hover:border-primary-300'}`}>
                          <span className="block text-sm font-medium">{slot.time}</span>
                          <span className={`text-xs ${!slot.available ? 'text-secondary-300' : selectedTime === slot.time ? 'text-white/70' : 'text-secondary-400'}`}>
                            {slot.available ? 'Available' : 'Booked'}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div><label className="label">Additional Notes</label><textarea className="textarea" rows="3" placeholder="Any specific questions or concerns?" value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
                </div>
              )}

              {/* Step 3: Review (logged in) OR Guest Details (not logged in) */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  {isLoggedIn ? (
                    <>
                      <div><h2 className="text-xl font-bold text-secondary-900 mb-1">Review Your Booking</h2><p className="text-secondary-500 text-sm">Please review before confirming</p></div>
                      <div className="space-y-4">
                        <div className="p-5 rounded-xl bg-secondary-50 border border-secondary-100">
                          <h3 className="font-semibold text-secondary-900 mb-3 flex items-center gap-2"><FiVideo className="w-4 h-4 text-primary-400" /> Service Details</h3>
                          {[
                            ['Service Type', serviceType === 'video' ? 'Video Call' : serviceType === 'phone' ? 'Phone Call' : 'In-Person'],
                            ['Duration', `${duration} minutes`],
                            ['Date', selectedDate],
                            ['Time', selectedTime],
                            ['Attorney', lawyerInfo?.name || 'N/A'],
                          ].map(([label, value]) => (
                            <div key={label} className="flex justify-between py-1.5"><span className="text-sm text-secondary-500">{label}</span><span className="text-sm font-medium text-secondary-900">{value}</span></div>
                          ))}
                        </div>
                        {description && <div className="p-5 rounded-xl bg-secondary-50 border border-secondary-100"><h3 className="font-semibold text-secondary-900 mb-2 text-sm">Your Issue</h3><p className="text-sm text-secondary-600">{description}</p></div>}
                        {notes && <div className="p-5 rounded-xl bg-secondary-50 border border-secondary-100"><h3 className="font-semibold text-secondary-900 mb-2 text-sm">Additional Notes</h3><p className="text-sm text-secondary-600">{notes}</p></div>}
                      </div>
                      <label className="flex items-start gap-3 cursor-pointer p-4 rounded-xl border border-secondary-200 hover:bg-secondary-50 transition-colors">
                        <input type="checkbox" checked={agreeTerms} onChange={(e) => setAgreeTerms(e.target.checked)} className="w-4 h-4 rounded border-secondary-300 text-primary-500 focus:ring-primary-500 mt-0.5" />
                        <span className="text-sm text-secondary-600">I agree to the booking terms and cancellation policy</span>
                      </label>
                    </>
                  ) : (
                    <>
                      <div><h2 className="text-xl font-bold text-secondary-900 mb-1">Your Details</h2><p className="text-secondary-500 text-sm">Please provide your details and verify your mobile number to confirm the booking</p></div>

                      {/* Booking Summary */}
                      <div className="p-4 rounded-xl bg-primary-50/50 border border-primary-100">
                        <h3 className="font-semibold text-secondary-900 mb-2 text-sm flex items-center gap-2"><FiCalendar className="w-4 h-4 text-primary-400" /> Booking Summary</h3>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                          <span className="text-secondary-500">Lawyer:</span><span className="font-medium text-secondary-900">{lawyerInfo?.name}</span>
                          <span className="text-secondary-500">Service:</span><span className="font-medium text-secondary-900">{serviceType === 'video' ? 'Video Call' : serviceType === 'phone' ? 'Phone Call' : 'In-Person'}</span>
                          <span className="text-secondary-500">Date:</span><span className="font-medium text-secondary-900">{selectedDate} at {selectedTime}</span>
                          <span className="text-secondary-500">Duration:</span><span className="font-medium text-secondary-900">{duration} min</span>
                        </div>
                      </div>

                      {/* Guest Form */}
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-secondary-700 mb-1">Full Name <span className="text-red-500">*</span></label>
                          <div className="relative">
                            <FiUser className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400" />
                            <input type="text" value={guestName} onChange={(e) => setGuestName(e.target.value)} placeholder="Enter your full name"
                              className="w-full pl-11 pr-4 py-3 border border-secondary-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all" />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-secondary-700 mb-1">Email Address <span className="text-red-500">*</span></label>
                          <div className="relative">
                            <FiMail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400" />
                            <input type="email" value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)} placeholder="Enter your email"
                              className="w-full pl-11 pr-4 py-3 border border-secondary-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all" />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-secondary-700 mb-1">Mobile Number <span className="text-red-500">*</span></label>
                          <div className="flex gap-2">
                            <div className="relative flex-1">
                              <FiSmartphone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400" />
                              <input type="tel" value={guestPhone} onChange={(e) => setGuestPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="10-digit mobile number" maxLength={10}
                                disabled={otpVerified}
                                className="w-full pl-11 pr-4 py-3 border border-secondary-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all disabled:bg-secondary-50 disabled:text-secondary-500" />
                            </div>
                            {!otpVerified && (
                              <button type="button" onClick={handleSendOtp} disabled={sendingOtp || guestPhone.length < 10 || !guestEmail}
                                className="px-5 py-3 bg-primary-500 text-white rounded-xl text-sm font-medium hover:bg-primary-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap">
                                {sendingOtp ? 'Sending...' : otpSent ? 'Resend OTP' : 'Send OTP'}
                              </button>
                            )}
                            {otpVerified && (
                              <div className="flex items-center gap-2 px-4 py-3 bg-teal-50 text-teal-600 rounded-xl text-sm font-medium">
                                <FiCheckCircle className="w-4 h-4" /> Verified
                              </div>
                            )}
                          </div>
                        </div>

                        {/* OTP Input */}
                        {otpSent && !otpVerified && (
                          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-2">
                            <label className="block text-sm font-medium text-secondary-700 mb-1">Enter OTP</label>
                            <div className="flex gap-2">
                              <input type="text" value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="Enter 6-digit OTP" maxLength={6}
                                className="flex-1 px-4 py-3 border border-secondary-200 rounded-xl text-sm font-mono tracking-widest text-center focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all" />
                              <button type="button" onClick={handleVerifyOtp} disabled={verifyingOtp || otpCode.length < 4}
                                className="px-5 py-3 bg-teal-500 text-white rounded-xl text-sm font-medium hover:bg-teal-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                                {verifyingOtp ? 'Verifying...' : 'Verify'}
                              </button>
                            </div>
                            <p className="text-xs text-secondary-400">OTP has been sent to your email address</p>
                          </motion.div>
                        )}
                      </div>

                      <label className="flex items-start gap-3 cursor-pointer p-4 rounded-xl border border-secondary-200 hover:bg-secondary-50 transition-colors">
                        <input type="checkbox" checked={agreeTerms} onChange={(e) => setAgreeTerms(e.target.checked)} className="w-4 h-4 rounded border-secondary-300 text-primary-500 focus:ring-primary-500 mt-0.5" />
                        <span className="text-sm text-secondary-600">I agree to the booking terms and cancellation policy</span>
                      </label>
                    </>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 mt-8 pt-6 border-t border-secondary-100">
                {currentStep > 1 && <button onClick={handlePrev} className="btn-ghost py-3 px-6 border border-secondary-200 gap-2"><FiArrowLeft className="w-4 h-4" /> Back</button>}
                <div className="flex-1" />
                {currentStep < 3 && <button onClick={handleNext} className="btn-primary py-3 px-8 gap-2">Next Step <FiArrowRight className="w-4 h-4" /></button>}
                {currentStep === 3 && (
                  <button onClick={handleBooking} disabled={submitting || !agreeTerms || (!isLoggedIn && !otpVerified)}
                    className="btn-primary py-3 px-8 gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                    {submitting ? (
                      <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Booking...</>
                    ) : (
                      <><FiCheckCircle className="w-4 h-4" /> Confirm Booking</>
                    )}
                  </button>
                )}
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Attorney */}
            <div className="bg-white rounded-2xl shadow-card border border-secondary-100 p-6">
              <h3 className="font-semibold text-secondary-900 mb-4">Attorney</h3>
              {lawyerLoading ? (
                <div className="flex items-center justify-center py-4">
                  <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                  <span className="ml-2 text-sm text-secondary-400">Loading...</span>
                </div>
              ) : lawyerInfo ? (
                <>
                  <div className="flex items-center gap-4 mb-4">
                    {lawyerInfo.avatar ? (
                      <img src={lawyerInfo.avatar} alt={lawyerInfo.name} className="w-14 h-14 rounded-xl object-cover ring-2 ring-primary-200" />
                    ) : (
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white text-xl font-bold shadow-md">
                        {lawyerInfo.name?.charAt(0) || '?'}
                      </div>
                    )}
                    <div>
                      <h4 className="font-semibold text-secondary-900">{lawyerInfo.name}</h4>
                      <p className="text-sm text-secondary-500">{lawyerInfo.specialty}</p>
                    </div>
                  </div>
                  {allLawyers.length > 1 && (
                    <div>
                      <label className="text-xs font-medium text-secondary-500 mb-1 block">Change Attorney</label>
                      <select value={selectedLawyerId} onChange={(e) => handleLawyerChange(e.target.value)}
                        className="w-full text-sm border border-secondary-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all bg-secondary-50">
                        {allLawyers.map(l => (
                          <option key={l.id} value={l.id}>{l.full_name} — {l.specialty || 'General'}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-secondary-400">No attorneys available</p>
              )}
            </div>

            {/* Cancellation */}
            <div className="bg-white rounded-2xl shadow-card border border-secondary-100 p-6">
              <h3 className="font-semibold text-secondary-900 mb-3 flex items-center gap-2"><FiShield className="w-4 h-4 text-primary-400" /> Cancellation Policy</h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm"><span className="text-secondary-500">Up to 24 hours</span><span className="text-teal-600 font-medium">Free</span></div>
                <div className="flex justify-between text-sm"><span className="text-secondary-500">Less than 24 hours</span><span className="text-accent-600 font-medium">50% charged</span></div>
              </div>
              <p className="text-xs text-secondary-400 mt-3">No-show will incur full charges</p>
            </div>

            {/* Contact Us */}
            <div className="bg-gradient-to-br from-primary-50 to-primary-100/50 rounded-2xl border border-primary-200/50 p-6 text-center">
              <div className="w-12 h-12 rounded-full bg-primary-500 flex items-center justify-center mx-auto mb-3"><FiPhone className="w-5 h-5 text-white" /></div>
              <h4 className="font-semibold text-secondary-900 mb-1">Need Help?</h4>
              <p className="text-xs text-secondary-500 mb-3">Call us for assistance with your booking</p>
              <a href="tel:+917970809406" className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-400 transition-colors">
                <FiPhone className="w-3.5 h-3.5" /> Call: 7970809406
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BookingModern;
